// Utilities extracted from AssessmentModule.js

import _ from "lodash";
import update from "immutability-helper";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import client from "lib/apolloClient/client";
import {
  getGradesTemplatesQuery,
  getAssessmentBasicDetailsQuery,
} from "../AssessmentQuery";

// list of groupType for which we currently support picking of template from lowest grade
const ENABLED_TASK_GROUP = ["LEARNING_ENGAGEMENT", "ASSESSMENT"];

const recursiveSearchAndRemove = ({ edges, parentId, assessmentToolId }) => {
  return _.map(edges, edge => {
    if (_.get(edge, "node.item.id") === parentId) {
      const currentEdges = _.get(edge, "node.item.assessmentTools.edges", []);
      const newEdges = _.filter(
        currentEdges,
        edge => _.get(edge, "node.assessmentToolItem.id") !== assessmentToolId
      );
      return update(edge, {
        node: {
          item: {
            assessmentTools: {
              edges: { $set: newEdges },
            },
          },
        },
      });
    }

    if (_.has(edge, "node.childrenV2.edges")) {
      return update(edge, {
        node: {
          childrenV2: {
            edges: {
              $set: recursiveSearchAndRemove({
                edges: edge.node.childrenV2.edges,
                parentId,
                assessmentToolId,
              }),
            },
          },
        },
      });
    }

    return edge;
  });
};

const recursiveSearchAndUpdate = ({
  edges,
  parentId,
  toolId,
  isTeacherEvaluationEnable,
  isStudentEvaluationEnable,
}) => {
  return _.map(edges, edge => {
    if (_.isEqual(_.get(edge, "node.item.id"), parentId)) {
      const assessmentTools = _.get(
        edge,
        "node.item.assessmentTools.edges",
        []
      );

      const updatedAssessmentTools = _.map(assessmentTools, assessmentTool => {
        if (_.get(assessmentTool, "node.assessmentToolItem.id") === toolId) {
          return update(assessmentTool, {
            node: {
              isTeacherEvaluationEnable: { $set: isTeacherEvaluationEnable },
              isStudentEvaluationEnable: { $set: isStudentEvaluationEnable },
            },
          });
        }
        return assessmentTool;
      });

      return update(edge, {
        node: {
          item: {
            assessmentTools: { edges: { $set: updatedAssessmentTools } },
          },
        },
      });
    }

    if (_.has(edge, "node.childrenV2.edges")) {
      return update(edge, {
        node: {
          childrenV2: {
            edges: {
              $set: recursiveSearchAndUpdate({
                edges: edge.node.childrenV2.edges,
                parentId,
                toolId,
                isTeacherEvaluationEnable,
                isStudentEvaluationEnable,
              }),
            },
          },
        },
      });
    }

    return edge;
  });
};

const _updateAssessmentTools = (
  assessmentTools,
  toolId,
  isTeacherEvaluationEnable,
  isStudentEvaluationEnable
) => {
  const updatedAssessmentTools = _.map(
    assessmentTools?.edges,
    assessmentTool => {
      const assessmentToolId = _.get(
        assessmentTool,
        "node.assessmentToolItem.id",
        ""
      );

      if (_.isEqual(assessmentToolId, toolId)) {
        if (!_.isNil(isTeacherEvaluationEnable)) {
          return update(assessmentTool, {
            node: {
              isTeacherEvaluationEnable: {
                $set: isTeacherEvaluationEnable,
              },
            },
          });
        }

        if (!_.isNil(isStudentEvaluationEnable)) {
          return update(assessmentTool, {
            node: {
              isStudentEvaluationEnable: {
                $set: isStudentEvaluationEnable,
              },
            },
          });
        }
      } else {
        return assessmentTool;
      }
    }
  );
  return {
    edges: updatedAssessmentTools,
    __typename: "AssessmentToolConnection",
  };
};

const processToolForCacheUpdate = (
  tool,
  parentAssessmentToolDetails,
  isWorksheetQueriesV2 = false
) => {
  if (tool?.parentType === "QUESTION") {
    if (isWorksheetQueriesV2) {
      const questions = _.get(parentAssessmentToolDetails, "questions.edges");
      const question = _.find(
        questions,
        ({ id }) => id === _.get(tool, "parentId")
      );
      const assessmentTools = _.get(question, "assessmentTools");
      _.set(question, "assessmentTools", {
        ...assessmentTools,
        edges: [],
        totalCount: 0,
      });

      return parentAssessmentToolDetails;
    } else {
      //WORKSHEET_RESTRUCTURING: Remove this block when queriesV2 is enabled
      const newOrganizationResources = _.map(
        _.get(
          parentAssessmentToolDetails,
          "organizationResourcesV2.edges[0].node.childrenV2.edges",
          []
        ),
        orgResource => {
          const newEdges = recursiveSearchAndRemove({
            edges: _.get(orgResource, "node.childrenV2.edges"),
            parentId: tool.parentId,
            assessmentToolId: tool.id,
          });

          return update(orgResource, {
            node: { childrenV2: { edges: { $set: newEdges } } },
          });
        }
      );

      return update(parentAssessmentToolDetails, {
        organizationResourcesV2: {
          edges: {
            [0]: {
              node: {
                childrenV2: { edges: { $set: newOrganizationResources } },
              },
            },
          },
        },
      });
    }
  } else {
    const assessmentToolNodes = _.filter(
      _.get(parentAssessmentToolDetails, "assessmentTools.edges", []),
      ({ node }) => node.assessmentToolItem.id !== tool.id
    );

    return {
      ...parentAssessmentToolDetails,
      assessmentTools: {
        edges: assessmentToolNodes,
        __typename: "AssessmentToolConnection",
      },
    };
  }
};

const FIELD_KEYS_TO_PICK = [
  "id",
  "value",
  "isComplete",
  "hasAddRemoveValues",
  "valuesToAdd",
  "valuesToRemove",
  "isDescriptionUpdateFromAIInput",
];

export const getGradesTemplates = async ({ variables }) => {
  try {
    const queryData = await client.query({
      query: getGradesTemplatesQuery,
      variables,
      fetchPolicy: "network-only",
    });

    return _.get(queryData, "data.node", {});
  } catch (error) {
    return {};
  }
};

export const getAssessmentBasicDetails = async variables => {
  try {
    const result = await client.query({
      query: getAssessmentBasicDetailsQuery,
      variables,
      fetchPolicy: "network-only",
    });
    return _.get(result, "data.node", {});
  } catch (err) {
    return {};
  }
};

const getPlannerTemplates = async ({
  isGradeWiseAssessment = false,
  gradeId,
  organizationId,
}) => {
  if (isGradeWiseAssessment && gradeId) {
    const queryData = await getGradesTemplates({
      variables: {
        id: gradeId,
        gradeTemplateFilters: {
          templateType: ENABLED_TASK_GROUP,
        },
      },
    });

    return _.get(queryData, "plannerTemplates", []);
  } else {
    // Fetching all templates from organization
    return _.get(
      getOrganizationDetailsFromCache(organizationId),
      "templates",
      []
    );
  }
};

export const getInitialStep = ({ creationMode, shouldCreateAssessment }) => {
  switch (creationMode) {
    case "CREATE_LE":
      return "LE_CREATION";

    case "CREATE_AI_TUTOR":
      return "AI_TUTOR_CREATION";

    case "CREATE_SA":
      return "SMT_CREATION";

    case "CREATE_FA":
      return "FMT_CREATION";

    case "CREATE_ASSESSMENT":
      return "ASSESSMENT_CREATION";

    case "CREATE_WORKSHEET":
      return "WORKSHEET_QUESTION_CREATION";

    case "IMPORT_LE_FROM_LE_LIBRARY":
      return shouldCreateAssessment
        ? "LE_LIBRARY_LE_SELECTION"
        : "ASSESSMENT_DETAILS";

    case "IMPORT_LE":
    case "UNIT":
      return shouldCreateAssessment ? "UNIT_SELECTION" : "ASSESSMENT_DETAILS";

    default:
      return null;
  }
};

// Auto-generated exports
export {
  ENABLED_TASK_GROUP,
  recursiveSearchAndRemove,
  recursiveSearchAndUpdate,
  _updateAssessmentTools,
  processToolForCacheUpdate,
  FIELD_KEYS_TO_PICK,
  getPlannerTemplates,
};
